import * as React from "react";
import clsx from "clsx";
import { motion } from "framer-motion";
import { SnapshotQaPanel } from "@/modules/snapshots/grid/sidebar/qa/SnapshotQaPanel";
import type { Citation } from "@/modules/snapshots/grid/sidebar/qa/types";

interface Props {
  showQaPanel: boolean;
  qaExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  businessReportId: string;
  snapshotCollectionId: string;
  subcollectionId: string | undefined;
  contractFamilyProjection: boolean;
  onCitationClick: (citation: Citation) => void;
  onOutcomeClick: (outcomeId: string, detectedFields: string[]) => void;
  onOpenQa: (outcomeId: string) => void;
  children: React.ReactNode;
}

/** The grid with the "Analyse" Q&A panel docked on its left, or expanded into a modal overlay. */
export const SnapshotQaSplitView: React.FC<Props> = ({
  showQaPanel,
  qaExpanded,
  onToggleExpand,
  onClose,
  businessReportId,
  snapshotCollectionId,
  subcollectionId,
  contractFamilyProjection,
  onCitationClick,
  onOutcomeClick,
  onOpenQa,
  children,
}) => (
  <>
    {showQaPanel && qaExpanded && <div className="fixed inset-0 z-50 bg-black/80" onClick={onToggleExpand} />}
    <div className="relative flex min-h-0 flex-grow overflow-hidden shadow ring-1 ring-[#d2cac0] sm:rounded-lg">
      {!qaExpanded && (
        <motion.div
          initial={false}
          animate={{
            width: showQaPanel ? "45%" : 0,
            opacity: showQaPanel ? 1 : 0,
          }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="shrink-0 overflow-hidden border-r"
        />
      )}
      {showQaPanel && (
        <div
          className={clsx(
            "flex flex-col overflow-hidden bg-background",
            qaExpanded
              ? "fixed left-1/2 top-1/2 z-50 h-[85vh] w-full max-w-4xl -translate-x-1/2 -translate-y-1/2 rounded-lg border shadow-lg"
              : "absolute border-r",
          )}
          style={!qaExpanded ? { height: "100%", width: "45%" } : undefined}
        >
          <SnapshotQaPanel
            businessReportId={businessReportId}
            snapshotCollectionId={snapshotCollectionId}
            subcollectionId={subcollectionId}
            activeFilters={null}
            onCitationClick={onCitationClick}
            onOutcomeClick={onOutcomeClick}
            onOpenQa={onOpenQa}
            isExpanded={qaExpanded}
            onToggleExpand={onToggleExpand}
            onClose={onClose}
            contractFamilyProjection={contractFamilyProjection}
          />
        </div>
      )}
      <div
        className="flex min-h-0 flex-grow flex-col overflow-hidden"
        style={{
          overscrollBehavior: "contain",
        }}
      >
        {children}
      </div>
    </div>
  </>
);
